import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { visionData } from '../data/mock';

gsap.registerPlugin(ScrollTrigger);

const VisionSection = () => {
  const sectionRef = useRef(null);
  const textRef = useRef(null);
  const quoteRef = useRef(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (isMobile) {
      // Show content immediately on mobile
      const words = quoteRef.current?.querySelectorAll('.word') || [];
      words.forEach(w => {
        w.style.opacity = '1';
        w.style.transform = 'none';
      });
      return;
    }

    // Parallax text effect (desktop only)
    gsap.to(textRef.current, {
      y: -80,
      ease: 'none',
      scrollTrigger: {
        trigger: sectionRef.current,
        start: 'top bottom',
        end: 'bottom top',
        scrub: 1
      }
    });

    // Quote reveal animation
    const words = quoteRef.current?.querySelectorAll('.word') || [];
    gsap.fromTo(
      words,
      { y: 40, opacity: 0 },
      {
        y: 0,
        opacity: 1,
        duration: 0.6,
        stagger: 0.05,
        ease: 'power3.out',
        scrollTrigger: {
          trigger: quoteRef.current,
          start: 'top 80%',
          toggleActions: 'play none none reverse'
        }
      }
    );

    return () => {
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, [isMobile]);

  // Split text into words for animation
  const splitIntoWords = (text) => {
    return text.split(' ').map((word, index) => (
      <span key={index} className="word inline-block mr-[0.3em]">
        {word}
      </span>
    ));
  };

  return (
    <section
      ref={sectionRef}
      id="about"
      className="section relative flex items-center overflow-hidden"
      style={{ background: '#D3FF62' }}
      data-testid="vision-section"
    >
      {/* Large Background Text */}
      {!isMobile && (
        <div
          ref={textRef}
          className="absolute inset-0 flex items-center justify-center pointer-events-none overflow-hidden"
        >
          <span className="text-[18vw] font-black text-[#004534]/10 whitespace-nowrap tracking-tighter">
            OMNISPORTSGROUP
          </span>
        </div>
      )}

      <div className="container relative z-10">
        <div className="max-w-5xl mx-auto text-center">
          {/* Section Label */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-[#004534]/60 font-semibold uppercase tracking-widest mb-8"
          >
            {visionData.title}
          </motion.p>

          {/* Main Quote */}
          <div ref={quoteRef} className="mb-12">
            <h2 className="display-medium text-[#004534] leading-tight">
              {splitIntoWords(visionData.statement)}
            </h2>
          </div>

          {/* Description */}
          <motion.p
            initial={isMobile ? {} : { opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="body-large text-[#004534]/70 max-w-3xl mx-auto mb-12"
          >
            {visionData.description}
          </motion.p>

          {/* Visual Divider */}
          <motion.div
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            transition={{ duration: 1, delay: 0.5 }}
            className="w-32 h-1 bg-[#004534] mx-auto rounded-full"
          />
        </div>
      </div>

      {/* Corner Decorations - hidden on mobile */}
      <div className="absolute top-8 left-8 w-16 h-16 border-l-4 border-t-4 border-[#004534]/20 hidden md:block" />
      <div className="absolute bottom-8 right-8 w-16 h-16 border-r-4 border-b-4 border-[#004534]/20 hidden md:block" />
    </section>
  );
};

export default VisionSection;
